import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupee } from "@/lib/constants";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    FunnelChart, Funnel, LabelList, PieChart, Pie,
} from "recharts";
import {
    CalendarCheck, IndianRupee, Clock, CheckCircle2, Star, Repeat,
    TrendingUp, Users, HardHat, Target, Loader2,
} from "lucide-react";

const COLORS = ["#F28C28", "#102A43", "#1c3b5a", "#f0a860", "#2d5580"];

export default function AdminDashboard() {
    const [d, setD] = useState(null);
    const [kpi, setKpi] = useState(null);
    const [an, setAn] = useState(null);

    useEffect(() => {
        api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
        api.get("/admin/kpis").then((r) => setKpi(r.data)).catch(() => {});
        api.get("/admin/analytics").then((r) => setAn(r.data)).catch(() => {});
    }, []);

    if (!d) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>;

    const metrics = [
        { label: "Total bookings", value: d.total_bookings, icon: CalendarCheck },
        { label: "Today", value: d.today_bookings, icon: Clock },
        { label: "Pending", value: d.pending_bookings, icon: Clock },
        { label: "Completed", value: d.completed_bookings, icon: CheckCircle2 },
        { label: "Revenue", value: rupee(d.revenue), icon: IndianRupee },
        { label: "Pro payouts", value: rupee(d.payouts), icon: HardHat },
        { label: "Contribution", value: rupee(d.contribution), icon: TrendingUp },
        { label: "Avg rating", value: d.avg_rating || "—", icon: Star },
        { label: "Repeat rate", value: `${d.repeat_rate}%`, icon: Repeat },
        { label: "Cancellation", value: `${d.cancellation_rate}%`, icon: CalendarCheck },
        { label: "Customers", value: d.total_customers, icon: Users },
        { label: "Professionals", value: d.total_pros, icon: HardHat },
    ];

    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Dashboard</h1>
            <p className="text-sm text-navy/55">HOMIIE pilot · Jodhpur</p>

            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {metrics.map((m) => {
                    const Icon = m.icon;
                    return (
                        <div key={m.label} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`metric-${m.label.split(" ")[0].toLowerCase()}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-navy/50">{m.label}</span>
                                <Icon size={16} className="text-orange" />
                            </div>
                            <p className="mt-1 font-heading text-xl font-extrabold text-navy">{m.value}</p>
                        </div>
                    );
                })}
            </div>

            {/* KPI targets */}
            {kpi && (
                <div className="mt-6 rounded-2xl border border-navy/10 bg-white p-5">
                    <div className="mb-4 flex items-center gap-2"><Target size={18} className="text-orange" /><h2 className="font-heading text-lg font-bold text-navy">MVP Pilot Targets</h2></div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {kpi.targets.map((t) => {
                            const pct = Math.min(100, Math.round((t.value / t.target) * 100));
                            return (
                                <div key={t.label}>
                                    <div className="mb-1 flex justify-between text-sm"><span className="text-navy/70">{t.label}</span><span className="font-bold text-navy">{t.value} / {t.target}</span></div>
                                    <div className="h-2 overflow-hidden rounded-full bg-navy/10"><div className="h-full rounded-full bg-orange transition-all" style={{ width: `${pct}%` }} /></div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Analytics */}
            {an && (
                <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <div className="rounded-2xl border border-navy/10 bg-white p-5">
                        <h2 className="mb-4 font-heading text-lg font-bold text-navy">Conversion Funnel</h2>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={an.funnel} layout="vertical" margin={{ left: 20 }}>
                                <XAxis type="number" hide />
                                <YAxis type="category" dataKey="stage" width={110} tick={{ fontSize: 12, fill: "#102A43" }} />
                                <Tooltip />
                                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                                    {an.funnel.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <p className="mt-2 text-sm text-navy/55">Average Order Value: <b className="text-navy">{rupee(an.aov)}</b></p>
                    </div>
                    <div className="rounded-2xl border border-navy/10 bg-white p-5">
                        <h2 className="mb-4 font-heading text-lg font-bold text-navy">Bookings by Source</h2>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={an.sources} dataKey="count" nameKey="source" outerRadius={85} label={(e) => e.source}>
                                    {an.sources.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
}
